/**
 * HyperDX Browser Observability Service
 * Provides session replay, error tracking, and distributed tracing for Nova UI
 *
 * Features:
 * - Session replay for debugging user issues
 * - Console capture for debugging
 * - Network request capture for performance monitoring
 * - Custom event tracking for auth flows
 * - GDPR-compliant consent handling
 *
 * Privacy Notes:
 * - Never sends PII (emails, names, phone numbers) directly
 * - Uses Firebase UID for user identification
 * - Respects GDPR consent settings
 */

import HyperDX from '@hyperdx/browser';
import { getStoredConsent } from '../analytics';
import { isRuntimeError, isLikelyValidationOr400Message } from '../../utils/hyperdx-error';

// Configuration
const HYPERDX_API_KEY = process.env.REACT_APP_HYPERDX_API_KEY || '';
// Use REACT_APP_FIREBASE_ENV which is set by CI/CD (values: 'dev', 'staging', 'prod')
const APP_ENV = process.env.REACT_APP_FIREBASE_ENV || process.env.REACT_APP_ENV || 'development';
const DEBUG_MODE = process.env.NODE_ENV === 'development';

// Storage key for HyperDX-specific consent (uses same as analytics)
const CONSENT_STORAGE_KEY = 'nova_analytics_consent';

// Track initialization state
let isInitialized = false;

/**
 * Check if HyperDX should be enabled based on environment and API key
 * Only enabled in PRODUCTION environment
 */
const shouldEnableHyperDX = (): boolean => {
  // Only enable in production (CI/CD sets 'prod', allow both 'prod' and 'production')
  if (APP_ENV !== 'prod' && APP_ENV !== 'production') {
    if (DEBUG_MODE) {
      console.log('[HyperDX] Not production environment, skipping initialization');
    }
    return false;
  }

  // Don't enable if no API key
  if (!HYPERDX_API_KEY) {
    console.warn('[HyperDX] Production environment but no API key configured');
    return false;
  }

  return true;
};

/**
 * Check if user has granted consent for observability
 */
const hasObservabilityConsent = (): boolean => {
  if (typeof window === 'undefined') return false;
  const consent = localStorage.getItem(CONSENT_STORAGE_KEY);
  return consent === 'granted';
};

/**
 * Initialize HyperDX browser SDK
 * Should be called after analytics initialization and consent check
 */
export const initializeHyperDX = (): void => {
  if (isInitialized) {
    if (DEBUG_MODE) {
      console.log('[HyperDX] Already initialized, skipping');
    }
    return;
  }

  if (!shouldEnableHyperDX()) {
    return;
  }

  // Check consent before initializing session replay
  const consent = getStoredConsent();

  try {
    // Don't send image load failures to HyperDX (prevents "eventListener.error" for broken images)
    if (typeof window !== 'undefined') {
      const win = window as Window & { __hyperdxImageErrorFilterAdded?: boolean };
      if (!win.__hyperdxImageErrorFilterAdded) {
        window.addEventListener(
          'error',
          (event: ErrorEvent) => {
            // Don't send image load failures to HyperDX
            if (event.target instanceof HTMLImageElement) {
              event.stopImmediatePropagation();
              event.preventDefault();
              return;
            }
            // Don't send any 4xx (client/validation/auth) errors to HyperDX
            const err = event.error as { status?: number; statusCode?: number; message?: string } | undefined;
            const status = err?.status ?? err?.statusCode;
            if (typeof status === 'number' && status >= 400 && status < 500) {
              event.stopImmediatePropagation();
              event.preventDefault();
              return;
            }
            // Optional: when status isn't on the error, filter by message (validation-like)
            const message = event.message || err?.message;
            if (isLikelyValidationOr400Message(message)) {
              event.stopImmediatePropagation();
              event.preventDefault();
            }
          },
          true
        );
        win.__hyperdxImageErrorFilterAdded = true;
      }
    }

    // Capture original console.error before HyperDX patches it
    const _originalConsoleError = console.error.bind(console);

    HyperDX.init({
      apiKey: HYPERDX_API_KEY,
      service: `nova-ui-${APP_ENV}`,
      // Enable session replay only if consent is granted
      disableIntercom: true,
      // Trace propagation targets - match your API endpoints
      tracePropagationTargets: [
        /api-.*\.a\.run\.app/i, // Matches staging/prod Cloud Run URLs
        /localhost:5001/i, // Local development
      ],
      // Capture console logs for debugging
      consoleCapture: consent === 'granted',
      // Capture network requests for performance monitoring
      advancedNetworkCapture: consent === 'granted',
      // Mask sensitive data in session replay
      maskAllInputs: true,
      maskAllText: false, // Don't mask all text, just inputs
    });

    // Never send console.error to HyperDX — only the original console (browser devtools).
    // Runtime errors still go to HyperDX via uncaught errors and trackHyperDXError.
    console.error = function (...args: unknown[]) {
      _originalConsoleError.apply(console, args);
    };

    isInitialized = true;

    if (DEBUG_MODE) {
      console.log('[HyperDX] Initialized for', APP_ENV, 'environment');
    }
  } catch (error) {
    console.error('[HyperDX] Failed to initialize:', error);
  }
};

/**
 * Set the current user for session tracking
 * @param userId - Firebase UID (NOT email or PII)
 * @param email - Optional email for display (will be hashed internally by HyperDX)
 */
export const setHyperDXUser = (userId: string, email?: string): void => {
  if (!isInitialized || !hasObservabilityConsent()) {
    if (DEBUG_MODE) {
      console.log('[HyperDX] Skipping setUser (not initialized or no consent)');
    }
    return;
  }

  try {
    HyperDX.setGlobalAttributes({
      userId,
      // Only set userEmail if provided and consent granted
      ...(email && { userEmail: email }),
    });

    if (DEBUG_MODE) {
      console.log('[HyperDX] User set:', userId.substring(0, 8) + '...');
    }
  } catch (error) {
    console.error('[HyperDX] Failed to set user:', error);
  }
};

/**
 * Clear user data on logout
 */
export const clearHyperDXUser = (): void => {
  if (!isInitialized) {
    return;
  }

  try {
    // Set empty strings to clear user data (HyperDX doesn't accept undefined)
    HyperDX.setGlobalAttributes({
      userId: '',
      userEmail: '',
    });

    if (DEBUG_MODE) {
      console.log('[HyperDX] User cleared');
    }
  } catch (error) {
    console.error('[HyperDX] Failed to clear user:', error);
  }
};

/**
 * Track a custom action/event
 * @param event - Event name (e.g., 'auth.signup_attempt')
 * @param data - Optional event data (no PII!)
 */
export const trackHyperDXAction = (
  event: string,
  data?: Record<string, string | number | boolean>
): void => {
  if (!isInitialized || !hasObservabilityConsent()) {
    if (DEBUG_MODE) {
      console.log('[HyperDX] Skipping action (not initialized or no consent):', event);
    }
    return;
  }

  try {
    HyperDX.addAction(event, data || {});

    if (DEBUG_MODE) {
      console.log('[HyperDX] Action tracked:', event, data);
    }
  } catch (error) {
    console.error('[HyperDX] Failed to track action:', error);
  }
};

/**
 * Track an error with context. Only sends to HyperDX for runtime/unexpected errors
 * (e.g. network failures, uncaught exceptions). Skips 4xx, validation, auth, code-thrown errors.
 * @param error - The error object
 * @param context - Additional context about where the error occurred (e.g. status from API)
 */
export const trackHyperDXError = (
  error: Error,
  context?: Record<string, string | number | boolean>
): void => {
  if (!isInitialized || !hasObservabilityConsent()) {
    if (DEBUG_MODE) {
      console.log('[HyperDX] Skipping error tracking (not initialized or no consent)');
    }
    return;
  }

  if (!isRuntimeError(error, context)) {
    if (DEBUG_MODE) {
      console.log('[HyperDX] Skipping error (expected/code-thrown):', error.name, error.message?.substring(0, 80));
    }
    return;
  }

  try {
    // Sanitize error message to remove potential PII
    const sanitizedMessage = error.message
      ?.replace(/[\w.-]+@[\w.-]+/g, '[EMAIL]') // Remove emails
      .replace(/\b\d{10,}\b/g, '[PHONE]') // Remove phone numbers
      .substring(0, 500); // Limit length

    HyperDX.addAction('error', {
      error_name: error.name,
      error_message: sanitizedMessage,
      error_stack: error.stack?.substring(0, 1000),
      ...context,
    });

    if (DEBUG_MODE) {
      console.log('[HyperDX] Error tracked (runtime):', error.name, context);
    }
  } catch (trackError) {
    console.error('[HyperDX] Failed to track error:', trackError);
  }
};

/**
 * Handle consent change (called from CookieConsentBanner)
 * @param granted - Whether consent was granted
 */
export const onConsentChange = (granted: boolean): void => {
  if (granted && !isInitialized) {
    // Initialize now that we have consent
    initializeHyperDX();
  } else if (!granted && isInitialized) {
    // User revoked consent - clear user data
    clearHyperDXUser();
    if (DEBUG_MODE) {
      console.log('[HyperDX] Consent revoked, user data cleared');
    }
  }
};

/**
 * Check if HyperDX is initialized
 */
export const isHyperDXInitialized = (): boolean => {
  return isInitialized;
};

/**
 * Export HyperDX service as default object
 */
const hyperdxService = {
  initializeHyperDX,
  setHyperDXUser,
  clearHyperDXUser,
  trackHyperDXAction,
  trackHyperDXError,
  onConsentChange,
  isHyperDXInitialized,
};

export default hyperdxService;
